// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: d699e5f8-5316-4e8e-80e7-961c1256cda8

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Source-cloud tag columns -> real Primordia tags, at import time
// ---------------------------------------------------------------------------
// A client moving Azure->AWS (or the reverse) already carries a tag discipline
// — environment, application, owner, cost center — in their inventory exports.
// Before this module those columns landed as custom-field text at best. This
// parses them into fs.tags [{group, value}] so the estate arrives pre-faceted.
// The existing save path (store.saveFactSheet -> backend replaceTags) already
// persists fs.tags and auto-creates tag groups, so everything here is pure
// parsing: no store, no React, no MUI — import-safe under plain node, which is
// how it is verified (there is no test runner in this project; see the
// examples block at the bottom, exercised with a `node -e` harness).
//
// Parsing is ADDITIVE and never destructive: the raw columns still land
// wherever they land today (annotation / cf_* custom fields). This module only
// produces extra tags to merge in.
//
// Recognized, case-insensitively:
//
//   1. PACKED columns — a column named "Tags" / "Tag" / "Resource tags" whose
//      cells hold packed pairs in the real-world shapes:
//        "Environment: Prod; App: Epic"      (Azure portal CSV — ; and :)
//        "env=prod,app=epic"                 (AWS-ish — , and =)
//        "env:prod|app:epic"                 (pipe form)
//      A cell is split on ; | or , ONLY when every non-empty segment has
//      key/value structure. A cell with no k/v structure ("decommission after
//      Q3", "http://wiki/page", "C:\\backups") is NOT a tag set — it is left
//      alone and keeps landing as whatever field the column maps to.
//
//   2. PER-TAG columns — headers "Tag:Key", "tag:Key", "Tags.Key", "tag_Key",
//      "aws:tag/Key". The Key becomes the tag group, the cell the value.
//
//   3. RVTools custom attributes — "Custom Attribute: X" / "CustomAttribute.X"
//      columns become group X. A column literally named "Annotation" is free
//      text and is deliberately NOT parsed as tags.
//
// Normalization: keys and values are trimmed; both must be non-empty; group
// names are capped at 40 characters and values at 120 (truncations are counted
// so the preview can announce them); pairs whose key starts with the reserved
// "aws:" system prefix are skipped (except the aws:tag/Key HEADER form above,
// where the part after aws:tag/ is the real key); (group, value) pairs are
// deduped per row.
// ---------------------------------------------------------------------------

export const MAX_GROUP_LEN = 40;
export const MAX_VALUE_LEN = 120;

const lower = (s) => String(s === undefined || s === null ? '' : s).toLowerCase();
const normHeader = (s) => lower(s).replace(/[^a-z0-9]/g, '');
const cellText = (v) => String(v === undefined || v === null ? '' : v).trim();

// Column names whose CELLS hold packed key/value pairs.
const PACKED_HEADER_NAMES = new Set(['tags', 'tag', 'resourcetags']);

// Header shapes where the KEY lives in the header itself.
const AWS_TAG_HEADER_RE = /^aws:tag\/(.+)$/i;                    // aws:tag/Key
const TAG_PREFIX_HEADER_RE = /^tags?\s*[:._]\s*(.+)$/i;          // Tag:Key Tags.Key tag_Key
const CUSTOM_ATTR_HEADER_RE = /^custom\s*attribute\s*[:._]?\s*(.+)$/i; // Custom Attribute: X / CustomAttribute.X

const RESERVED_KEY_RE = /^aws:/i;

// A URI scheme ("http://…") or a Windows path ("C:\…") is not a tag pair even
// though it contains a colon.
const URI_SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

// ---------------------------------------------------------------------------
// detectTagColumns(headers) -> { columns, packed, perTag }
//   columns: [{ header, kind: 'packed'|'perTag'|'customAttr', group? }]
//   (packed/perTag are the same objects, pre-split for convenience)
// Reserved per-tag headers (a Key that still starts with "aws:") are excluded
// and listed under `reserved` so a preview can say why a column was ignored.
// ---------------------------------------------------------------------------
export function detectTagColumns(headers) {
  const columns = [];
  const reserved = [];
  (headers || []).forEach((h) => {
    const header = String(h === undefined || h === null ? '' : h).trim();
    if (!header) return;
    if (PACKED_HEADER_NAMES.has(normHeader(header))) {
      columns.push({ header, kind: 'packed' });
      return;
    }
    let m = AWS_TAG_HEADER_RE.exec(header);
    let kind = 'perTag';
    if (!m) m = TAG_PREFIX_HEADER_RE.exec(header);
    if (!m) { m = CUSTOM_ATTR_HEADER_RE.exec(header); if (m) kind = 'customAttr'; }
    if (!m) return; // "Annotation", "Tagging Status", ordinary columns: not tags
    const group = m[1].trim();
    if (!group) return;
    if (RESERVED_KEY_RE.test(group)) { reserved.push({ header, group }); return; }
    columns.push({ header, kind, group });
  });
  return {
    columns,
    packed: columns.filter((c) => c.kind === 'packed'),
    perTag: columns.filter((c) => c.kind !== 'packed'),
    reserved,
  };
}

// One segment of a packed cell -> { pair, key, value, reserved }.
// pair:false means "this has no k/v structure", which disqualifies the whole
// cell (see parsePackedCell). A reserved aws:* key IS pair-shaped — it counts
// toward the cell being a tag set, it just does not produce a tag.
function parseSegment(seg) {
  const s = seg.trim();
  if (!s) return null; // empty segment (trailing separator) — ignored entirely
  if (RESERVED_KEY_RE.test(s)) return { pair: true, reserved: true };
  if (URI_SCHEME_RE.test(s)) return { pair: false };
  const ci = s.indexOf(':');
  const ei = s.indexOf('=');
  const idx = ci >= 0 && ei >= 0 ? Math.min(ci, ei) : (ci >= 0 ? ci : ei);
  if (idx <= 0) return { pair: false }; // no delimiter, or empty key
  const key = s.slice(0, idx).trim();
  const value = s.slice(idx + 1).trim();
  if (!key || !value) return { pair: false };
  if (s[idx] === ':' && value[0] === '\\') return { pair: false }; // C:\path
  return { pair: true, key, value };
}

// A packed cell -> { isTagSet, pairs: [{key,value}], reserved: n }.
// isTagSet is true only when the cell has at least one non-empty segment and
// EVERY non-empty segment is pair-shaped. Anything else is free text and the
// caller must leave it alone.
export function parsePackedCell(raw) {
  const s = cellText(raw);
  const none = { isTagSet: false, pairs: [], reserved: 0 };
  if (!s) return none;
  const sep = s.indexOf(';') >= 0 ? ';' : (s.indexOf('|') >= 0 ? '|' : ',');
  const segments = s.split(sep);
  const pairs = [];
  let reserved = 0;
  let nonEmpty = 0;
  for (let i = 0; i < segments.length; i += 1) {
    const parsed = parseSegment(segments[i]);
    if (parsed === null) continue; // blank segment
    nonEmpty += 1;
    if (!parsed.pair) return none; // one free-text segment poisons the cell
    if (parsed.reserved) reserved += 1;
    else pairs.push({ key: parsed.key, value: parsed.value });
  }
  if (!nonEmpty) return none;
  return { isTagSet: true, pairs, reserved };
}

// Trim/cap/skip-empty one candidate pair. Returns null when it cannot be a
// tag; otherwise { group, value, truncatedGroup, truncatedValue }.
function normalizePair(key, value) {
  let group = cellText(key);
  let val = cellText(value);
  if (!group || !val) return null;
  if (RESERVED_KEY_RE.test(group)) return null;
  const truncatedGroup = group.length > MAX_GROUP_LEN;
  const truncatedValue = val.length > MAX_VALUE_LEN;
  if (truncatedGroup) group = group.slice(0, MAX_GROUP_LEN).trim();
  if (truncatedValue) val = val.slice(0, MAX_VALUE_LEN).trim();
  if (!group || !val) return null;
  return { group, value: val, truncatedGroup, truncatedValue };
}

const pairKey = (g, v) => `${g}\u0000${v}`;

// Full detail for one row — the summary needs the truncation/reserved counts,
// the import writer only needs .tags (see parseRowTags below).
export function parseRowTagsDetailed(row, detected) {
  const tags = [];
  const seen = new Set();
  let truncatedGroups = 0;
  let truncatedValues = 0;
  let reserved = 0;
  const add = (key, value) => {
    const t = normalizePair(key, value);
    if (!t) return;
    const k = pairKey(t.group, t.value);
    if (seen.has(k)) return; // dedupe (group, value) per row
    seen.add(k);
    if (t.truncatedGroup) truncatedGroups += 1;
    if (t.truncatedValue) truncatedValues += 1;
    tags.push({ group: t.group, value: t.value });
  };
  const r = row || {};
  const det = detected || { packed: [], perTag: [] };
  (det.packed || []).forEach((col) => {
    const parsed = parsePackedCell(r[col.header]);
    if (!parsed.isTagSet) return;
    reserved += parsed.reserved;
    parsed.pairs.forEach((p) => add(p.key, p.value));
  });
  (det.perTag || []).forEach((col) => add(col.group, r[col.header]));
  return { tags, truncatedGroups, truncatedValues, reserved };
}

// parseRowTags(row, detected) -> [{ group, value }] — what the import writes.
export function parseRowTags(row, detected) {
  return parseRowTagsDetailed(row, detected).tags;
}

// Merge parsed tags into whatever tags the flow already sets, deduping on
// (group, value). Never mutates either input — `existing` may be a frozen
// array off a store row.
export function mergeTags(existing, parsed) {
  const out = [];
  const seen = new Set();
  (existing || []).forEach((t) => {
    if (!t || !t.group || !t.value) return;
    const k = pairKey(String(t.group), String(t.value));
    if (seen.has(k)) return;
    seen.add(k);
    out.push(t);
  });
  (parsed || []).forEach((t) => {
    if (!t || !t.group || !t.value) return;
    const k = pairKey(String(t.group), String(t.value));
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ group: t.group, value: t.value });
  });
  return out;
}

// ---------------------------------------------------------------------------
// summarizeTags(rows, detected) — the preview's "Tags detected" numbers.
// Computed over EVERY row (counts are exact, only the rendering is capped by
// the caller). Includes the honesty case: a packed column that exists but
// whose cells never parse (packedStats.parsedCells === 0 with nonEmptyCells >
// 0 -> "column found but no key:value pairs recognized").
// ---------------------------------------------------------------------------
export function summarizeTags(rows, detected) {
  const det = detected || { columns: [], packed: [], perTag: [], reserved: [] };
  const byGroup = new Map(); // group -> { rows: n, values: Set }
  const distinctPairs = new Set();
  let rowsWithTags = 0;
  let truncatedGroups = 0;
  let truncatedValues = 0;
  let reservedSkipped = 0;
  const packedStats = (det.packed || []).map((c) => ({ header: c.header, nonEmptyCells: 0, parsedCells: 0 }));

  (rows || []).forEach((row) => {
    packedStats.forEach((st) => {
      const raw = cellText((row || {})[st.header]);
      if (!raw) return;
      st.nonEmptyCells += 1;
      if (parsePackedCell(raw).isTagSet) st.parsedCells += 1;
    });
    const d = parseRowTagsDetailed(row, det);
    truncatedGroups += d.truncatedGroups;
    truncatedValues += d.truncatedValues;
    reservedSkipped += d.reserved;
    if (!d.tags.length) return;
    rowsWithTags += 1;
    const groupsInRow = new Set();
    d.tags.forEach((t) => {
      distinctPairs.add(pairKey(t.group, t.value));
      let g = byGroup.get(t.group);
      if (!g) { g = { rows: 0, values: new Set() }; byGroup.set(t.group, g); }
      g.values.add(t.value);
      groupsInRow.add(t.group);
    });
    groupsInRow.forEach((name) => { byGroup.get(name).rows += 1; });
  });

  const groups = [...byGroup.entries()]
    .map(([group, g]) => ({ group, rows: g.rows, values: g.values.size }))
    .sort((a, b) => b.rows - a.rows || a.group.localeCompare(b.group));

  return {
    columns: det.columns || [],
    reserved: det.reserved || [],
    rowCount: (rows || []).length,
    rowsWithTags,
    distinctGroups: groups.length,
    distinctPairs: distinctPairs.size,
    groups,
    truncatedGroups,
    truncatedValues,
    reservedSkipped,
    packedStats,
    // The honesty case: a Tags-named column exists but zero cells parse.
    unparsedPacked: packedStats.filter((s) => s.nonEmptyCells > 0 && s.parsedCells === 0),
  };
}

/* ---------------------------------------------------------------------------
   Worked examples (there is no test runner in this project — CRA's build is
   the only frontend gate — so these are the parser's contract, verified with
   a `node -e` harness against the built module during development):

   detectTagColumns(['VM', 'Tags', 'Tag:Application', 'aws:tag/CostCenter',
                     'Tags.env', 'tag_owner', 'Custom Attribute: Backup',
                     'CustomAttribute.Patch Window', 'Annotation',
                     'aws:tag/aws:autoscaling:groupName', 'Resource tags'])
     -> packed:   Tags, Resource tags
        perTag:   Tag:Application (group Application),
                  aws:tag/CostCenter (group CostCenter),
                  Tags.env (group env), tag_owner (group owner)
        customAttr: Custom Attribute: Backup (group Backup),
                  CustomAttribute.Patch Window (group "Patch Window")
        reserved: aws:tag/aws:autoscaling:groupName (Key still aws:* — skipped)
        NOT recognized: VM, Annotation (free text, never tags)

   parsePackedCell('Environment: Prod; App: Epic; CostCenter: 1234')
     -> pairs [Environment/Prod, App/Epic, CostCenter/1234]   (Azure ; and :)
   parsePackedCell('env=prod,app=epic')
     -> pairs [env/prod, app/epic]                            (AWS , and =)
   parsePackedCell('env:prod|app:epic')
     -> pairs [env/prod, app/epic]                            (pipe form)
   parsePackedCell('env=prod,aws:cloudformation:stack-name=web')
     -> pairs [env/prod], reserved 1   (aws:* key skipped, cell still a set)
   parsePackedCell('decommission after Q3')   -> isTagSet false (no k/v)
   parsePackedCell('Prod; Env: Test')         -> isTagSet false (mixed: one
                                                 free-text segment poisons it)
   parsePackedCell('http://wiki/page')        -> isTagSet false (URI, not k/v)
   parsePackedCell('C:\\backups\\nightly')    -> isTagSet false (drive path)
   parsePackedCell('runbook: https://wiki/x') -> pairs [runbook/https://wiki/x]
                                                 (URL as a VALUE is fine)
   parsePackedCell('')                        -> isTagSet false

   parseRowTags({ 'Tag:Application': 'Epic' }, detected)
     -> [{ group: 'Application', value: 'Epic' }]
   parseRowTags({ Tags: 'a: 1; a: 1; b: 2' }, detected)
     -> [a/1, b/2]                                  (deduped per row)
   parseRowTags({ Tags: '  ' }, detected) -> []      (blank cell)
   A group over 40 chars / value over 120 chars is truncated and counted, so
   the preview can announce "N names were truncated".

   mergeTags([{group:'Env',value:'Prod'}], [{group:'Env',value:'Prod'},
             {group:'App',value:'Epic'}])
     -> [Env/Prod, App/Epic]                        (additive, deduped)
--------------------------------------------------------------------------- */
